import translate from '@translate';
import React, { FC, useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import FormBlock from 'src/components/FormBlock';
import { setLocationParams } from 'src/resources/location/reducer';
import { RootStoreType } from 'src/types/store';
import { checkFormValidity, getEditData } from 'src/utils/formFunctions';
import Loading from 'src/components/Loading/intex';
import { useCheckPermission } from 'src/utils/checkPermission';
import Detail from './Detail';
import { useNavigate } from 'react-router-dom';
import TopBlock from './TopBlock';
import {
    clearRoleDetail,
    createRoleDetailFetch,
    getRoleDetailFetch,
    updateRoleDetailFetch,
} from 'src/resources/roleDetail/reducer';
import { RoleType } from 'src/types/role';
import Tree from 'src/components/Tree';
import { getPermissionsListFetch } from 'src/resources/permissionsList/reducer';
import { SelectedItemsType } from 'src/components/Tree/Select';
import { addMessage } from 'src/resources/alertMessages/reducer';
import ConfirmDialog, { ConfirmDialogStatePropsType } from 'src/components/ConfirmDialog';
import { FormErrorsType, OnChangeType } from '../../types/global';

export type EditKeysType = 'ROLE_NAME' | 'ROLE_INFO';

export const EDIT_KEYS = ['ROLE_NAME', 'ROLE_INFO'];

export const PERMISSION_ID_KEY = 'PID';

const initData = { permissions: [] };

const RoleDetailContainer: FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { idRole } = useParams();

    const newRole = !idRole;

    const formRef = useRef<HTMLFormElement>(null);

    const data = useSelector((state: RootStoreType) => state.roleDetail.data);
    const loading = useSelector((state: RootStoreType) => state.roleDetail.loading);
    const loadingPermissionsList = useSelector((state: RootStoreType) => state.permissionsList.loading);
    const permissionsList = useSelector((state: RootStoreType) => state.permissionsList.data);

    const [editData, setEditData] = useState<RoleType>(getEditData(initData, EDIT_KEYS));
    const [editErrors, setEditErrors] = useState<FormErrorsType>({});
    const [selectedPermissions, setSelectedPermissions] = useState<SelectedItemsType>({});
    const [confirmParams, setConfirmParams] = useState<ConfirmDialogStatePropsType | null>(null);

    const disableEdit = newRole ? !useCheckPermission('ROLE.CREATE') : !useCheckPermission('ROLE.UPDATE');

    const backUrl = '/admin/roles/';

    useEffect(() => {
        if (idRole) {
            dispatch(setLocationParams({ name: translate('roleDetail.title', { idRole }), backUrl }));
        } else {
            dispatch(setLocationParams({ name: translate('roleDetail.titleNew'), backUrl }));
        }
    }, [idRole, backUrl]);

    useEffect(() => {
        if (idRole) {
            dispatch(getRoleDetailFetch(idRole));
        } else {
            setEditData(initData);
        }
        return () => {
            dispatch(clearRoleDetail());
        };
    }, [idRole]);

    useEffect(() => {
        if (data) {
            setEditData(data);
            const newSelectedPermissions: SelectedItemsType = {};
            data.permissions?.forEach((permission) => {
                permission[PERMISSION_ID_KEY] && (newSelectedPermissions[permission[PERMISSION_ID_KEY]] = true);
            });
            setSelectedPermissions(newSelectedPermissions);
        }

        if (!idRole && data?.ID_ROLE) {
            navigate('/admin/roles/' + String(data.ID_ROLE));
        }
    }, [data]);

    useEffect(() => {
        if (!permissionsList) {
            dispatch(getPermissionsListFetch());
        }
    }, [permissionsList]);

    const onChange: OnChangeType<EditKeysType> = (key, value, error) => {
        const newData = { ...editData };
        const newErrors = { ...editErrors };
        newData[key] = value;
        newErrors[key] = error;
        setEditData(newData);
        setEditErrors(newErrors);
    };

    const handleSave = () => {
        const newFormErrors = checkFormValidity(formRef);

        if (!Object.keys(newFormErrors).length) {
            setConfirmParams({
                title: translate(newRole ? 'roleDetail.create' : 'roleDetail.save'),
                content: translate(newRole ? 'roleDetail.createConfirm' : 'roleDetail.saveConfirm'),
                action: () => {
                    const formatData = { ...editData };
                    formatData.permissions = permissionsList?.filter(
                        (permission) =>
                            permission[PERMISSION_ID_KEY] && selectedPermissions[permission[PERMISSION_ID_KEY]]
                    );

                    if (newRole) {
                        dispatch(createRoleDetailFetch(formatData));
                    } else {
                        dispatch(updateRoleDetailFetch({ idRole, data: formatData }));
                    }
                },
            });
        } else {
            dispatch(
                addMessage({
                    type: 'warning',
                    title: translate('messages.formWarningTitle'),
                    text: translate('messages.formWarningText'),
                })
            );
        }

        setEditErrors(newFormErrors);
    };

    return (
        <>
            <TopBlock newRole={newRole} disableEdit={disableEdit} handleSave={handleSave} />
            <form ref={formRef}>
                {loading && <Loading />}

                <FormBlock>
                    <Detail
                        onChange={onChange}
                        editData={editData}
                        editErrors={editErrors}
                        newRole={newRole}
                        disableEdit={disableEdit}
                    />
                </FormBlock>
            </form>
            {permissionsList && (
                <Tree
                    loading={loadingPermissionsList}
                    data={permissionsList}
                    treeKey={PERMISSION_ID_KEY}
                    defaultTitle={translate('roles.title')}
                    selected={selectedPermissions}
                    setSelected={setSelectedPermissions}
                    disabledSelect={disableEdit}
                />
            )}
            {confirmParams && (
                <ConfirmDialog
                    title={confirmParams.title}
                    content={confirmParams.content}
                    action={confirmParams.action}
                    onClose={(): any => setConfirmParams(null)}
                />
            )}
        </>
    );
};

export default RoleDetailContainer;
